/**
 * PrenotazioniList Component - Hedita Design System
 * Feature: Prenotazioni - Lista prenotazioni
 * Stile: Premium Orange Dark Theme
 */

import { useState } from "react";
import { Plus, Search, CalendarDays, ChevronRight, ArrowUpDown, Filter, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { usePrenotazioni, usePrenotazioniStats, useAggiornaStatoPrenotazione } from "../hooks";
import type { Prenotazione } from "@/types";

const STATI = ["Attesa di conferma", "Confermata", "Check-in online effettuato", "In corso", "Check-out effettuato", "Completata", "Cancellata", "No show"];

const STATO_CONFIG: Record<string, { bg: string; color: string; border: string; dot: string }> = {
  "Attesa di conferma": { bg: "bg-orange-500/10", color: "text-orange-400", border: "border-orange-500/20", dot: "bg-orange-400" },
  "Confermata": { bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20", dot: "bg-sky-400" },
  "Check-in online effettuato": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", dot: "bg-emerald-400" },
  "In corso": { bg: "bg-primary/10", color: "text-primary", border: "border-primary/20", dot: "bg-primary" },
  "Check-out effettuato": { bg: "bg-purple-500/10", color: "text-purple-400", border: "border-purple-500/20", dot: "bg-purple-400" },
  "Completata": { bg: "bg-muted/30", color: "text-muted-foreground", border: "border-border", dot: "bg-muted-foreground" },
  "Cancellata": { bg: "bg-rose-500/10", color: "text-rose-400", border: "border-rose-500/20", dot: "bg-rose-400" },
  "No show": { bg: "bg-slate-700/50", color: "text-slate-400", border: "border-slate-600", dot: "bg-slate-500" },
};

const PIATTAFORMA_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  "Airbnb": { bg: "bg-rose-500/10", color: "text-rose-400", border: "border-rose-500/20" },
  "Booking.com": { bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20" },
  "Vrbo": { bg: "bg-violet-500/10", color: "text-violet-400", border: "border-violet-500/20" },
  "Diretto": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20" },
};

const PIATTAFORMA_FALLBACK = [
  { bg: "bg-cyan-500/10", color: "text-cyan-400", border: "border-cyan-500/20" },
  { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20" },
  { bg: "bg-pink-500/10", color: "text-pink-400", border: "border-pink-500/20" },
  { bg: "bg-indigo-500/10", color: "text-indigo-400", border: "border-indigo-500/20" },
];

function getPiattaformaStyle(p: string) {
  if (PIATTAFORMA_STYLES[p]) return PIATTAFORMA_STYLES[p];
  const hash = p.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PIATTAFORMA_FALLBACK[hash % PIATTAFORMA_FALLBACK.length];
}

function fmtDate(d: string) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

function notti(cin: string, cout: string) {
  const diff = (new Date(cout).getTime() - new Date(cin).getTime()) / 86400000;
  return isNaN(diff) ? 0 : Math.max(0, diff);
}

function fmtEur(n: number | null | undefined) {
  if (n == null) return "—";
  return "€ " + Number(n).toLocaleString("it", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function PrenotazioniList() {
  const [search, setSearch] = useState("");
  const [statoFilter, setStatoFilter] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const { data: prenotazioni = [], isLoading } = usePrenotazioni();
  const { data: stats } = usePrenotazioniStats();
  const aggiornaStato = useAggiornaStatoPrenotazione();

  const canali = [...new Set(prenotazioni.map(p => p.canale || "Diretto"))];

  const filtered = prenotazioni.filter(p => {
    const matchSearch = !search || 
      p.ospite_nome?.toLowerCase().includes(search.toLowerCase()) ||
      p.ospite_cognome?.toLowerCase().includes(search.toLowerCase()) ||
      p.struttura_nome?.toLowerCase().includes(search.toLowerCase());
    const matchStato = !statoFilter || p.stato === statoFilter;
    return matchSearch && matchStato;
  });

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-40 mb-2" />
            <Skeleton className="h-4 w-56" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Prenotazioni</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gestisci le prenotazioni delle strutture</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition-all ${
              showFilters ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>
          <button 
            onClick={() => window.location.href = "/prenotazioni/nuovo"}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
          >
            <Plus className="h-4 w-4" /> Nuova
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Totali", value: stats?.totali || 0, color: "text-foreground" },
          { label: "Attive", value: stats?.attive || 0, color: "text-primary" },
          { label: "In arrivo", value: stats?.inArrivo || 0, color: "text-emerald-400" },
          { label: "Completate", value: stats?.completate || 0, color: "text-muted-foreground" },
        ].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 text-center hover:border-primary/20 transition-all">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-xl border border-border/50">
          <button 
            onClick={() => setStatoFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              !statoFilter ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
            }`}
          >
            Tutti
          </button>
          {STATI.map(s => (
            <button 
              key={s} 
              onClick={() => setStatoFilter(statoFilter === s ? null : s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${
                statoFilter === s 
                  ? `${STATO_CONFIG[s].bg} ${STATO_CONFIG[s].color} ${STATO_CONFIG[s].border}` 
                  : "border-border text-muted-foreground"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${STATO_CONFIG[s].dot}`} />
              {s}
            </button>
          ))}
          {statoFilter && (
            <button 
              onClick={() => setStatoFilter(null)}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca per ospite, struttura..."
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
        />
      </div>

      {/* Lista */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDays className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-foreground mb-1">Nessuna prenotazione</p>
            <p className="text-sm text-muted-foreground mb-4">Crea la prima prenotazione</p>
            <button 
              onClick={() => window.location.href = "/prenotazioni/nuovo"}
              className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
            >
              Nuova prenotazione
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {filtered.map((p: Prenotazione) => {
              const stato = STATO_CONFIG[p.stato || "Confermata"] || STATO_CONFIG["Confermata"];
              const piatt = getPiattaformaStyle(p.canale || "Diretto");
              const nights = notti(p.check_in || "", p.check_out || "");
              
              return (
                <div 
                  key={p.id} 
                  onClick={() => window.location.href = `/prenotazioni/lista/${p.id}`}
                  className="p-4 hover:bg-secondary/30 transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Top row */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${stato.bg} ${stato.color} ${stato.border} flex items-center gap-1.5`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${stato.dot}`} />
                          {p.stato || "Confermata"}
                        </span>
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${piatt.bg} ${piatt.color} ${piatt.border}`}>
                          {p.canale || "Diretto"}
                        </span>
                        {p.struttura_nome && (
                          <span className="text-xs text-muted-foreground">{p.struttura_nome}</span>
                        )}
                      </div>
                      
                      {/* Guest name */}
                      <h3 className="font-semibold text-foreground text-sm mb-1">
                        {p.ospite_nome} {p.ospite_cognome}
                      </h3>
                      
                      {/* Dates */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" />
                        <span>{fmtDate(p.check_in || "")}</span>
                        <ChevronRight className="h-3 w-3" />
                        <span>{fmtDate(p.check_out || "")}</span>
                        <span className="px-1.5 py-0.5 rounded bg-secondary/50">{nights} notti</span>
                      </div>
                    </div>
                    
                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-foreground text-sm">{fmtEur(p.importo_totale)}</p>
                      <p className="text-xs text-muted-foreground">Totale</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
